import React, { useEffect, useMemo, useRef, useState } from "react";
import ExifReader from "exifreader";
import imageCompression from "browser-image-compression";
import {
  FaCheck, FaCheckCircle, FaLock, FaCloudUploadAlt, FaTrash, FaSpinner, FaDownload,
  FaExclamationTriangle, FaFilePdf, FaMapMarkerAlt,
} from "react-icons/fa";
import axiosConn from "config/axiosConn";
import * as gps from "../gpsFallbackUtils";
import {
  useSubmitBricsWeekReportMutation,
  useSubmitPublicBricsWeekReportMutation,
} from "../../../redux/auth/authApi";
import "./BricsWeekReportForm.css";

/*
 * BRICS Sustainable Lifestyle Week — the 8-question School Reporting form.
 *
 *   Q1  one-hour switch-off, daily for a week
 *   Q2  September electricity consumption (compared against November later)
 *   Q3  four-bin segregation — read back from the school's SWM report
 *   Q4  Eco Club notification on the notice board — read back from school_lists
 *   Q5  posters and theme details on the notice board
 *   Q6  activities held on the Mission LiFE themes
 *   Q7  participation (students and teachers)
 *   Q8  2-5 geotagged photographs
 *
 * Q3 and Q4 are the reason this form is short: ~1.7 lakh schools have already
 * answered them elsewhere, so when the server sends an answer back it is shown
 * as a locked chip and cannot be edited here. The server ignores the submitted
 * value for a locked question, so the lock is not merely a UI courtesy.
 */

const UNITS_MAX = 10000;
const PARTICIPANTS_MAX = 1000;
const PHOTOS_MIN = 2;
const PHOTOS_MAX = 5;
const PHOTO_MB = 5;

// SWM Rules, 2026 — the bins a school is asked to describe. Both columns stay
// editable: states run different colour conventions and the school must report
// what is actually painted on its bins.
const BIN_SLOTS = [
  { key: "green", colour: "Green", category: "Wet / biodegradable waste", swatch: "#2f9a3d" },
  { key: "blue", colour: "Blue", category: "Dry recyclable waste", swatch: "#2a6fdb" },
  { key: "black", colour: "Black", category: "Domestic hazardous waste", swatch: "#333a37" },
  { key: "red", colour: "Red", category: "Sanitary / biomedical waste", swatch: "#d94a3d" },
];

// Q6 — activities conducted on the Mission LiFE themes. Keys must match
// WEEK_ACTIVITIES in Backend2/Controllers/BricsCampaignController.js.
const ACTIVITIES = [
  { key: "poster_making", label: "Poster Making" },
  { key: "painting", label: "Painting Competition" },
  { key: "rangoli", label: "Rangoli" },
  { key: "slogan_writing", label: "Slogan Writing" },
  { key: "other", label: "Others" },
];
const ACTIVITY_OTHER_MAX = 120;

const NOTIFICATION_TEMPLATE =
  "https://aictevoicestaanuvadini.blob.core.windows.net/ecoclubs/Eco%20Club%20formation_Noti.docx?sp=r&st=2026-02-06T06:00:58Z&se=2026-12-31T14:15:58Z&spr=https&sv=2024-11-04&sr=c&sig=n4zcC%2FICuzy6IgQ62mGtREv%2FQI3HUeu2TFULEdZkDyI%3D";

function extractGPS(tag, refTag) {
  if (!tag) return NaN;
  let dec = typeof tag.description === "number" ? tag.description : parseFloat(tag.description);
  if (!Number.isFinite(dec) && Array.isArray(tag.value)) {
    const toNum = (x) => (Array.isArray(x) ? (x[1] ? x[0] / x[1] : x[0]) : Number(x));
    const d = toNum(tag.value[0]);
    const m = tag.value.length > 1 ? toNum(tag.value[1]) : 0;
    const s = tag.value.length > 2 ? toNum(tag.value[2]) : 0;
    dec = d + m / 60 + s / 3600;
  }
  if (!Number.isFinite(dec)) return NaN;
  const ref = String(refTag?.description ?? refTag?.value ?? "").trim().toUpperCase();
  if (ref.startsWith("S") || ref.startsWith("W")) dec = -Math.abs(dec);
  return dec;
}

const uploadFiles = async (formData, endpoint = "upload-collaboration-file") => {
  const res = await axiosConn("post", endpoint, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const urls = res?.data?.status === "success" ? res.data.data || [] : [];
  if (!urls.length) throw new Error(res?.data?.message || "Upload failed. Please try again.");
  return urls;
};

const safeName = (file, tag) => {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const base = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || tag;
  return `${base}_${new Date().toISOString().replace(/[:.]/g, "-")}_bslw26.${ext}`;
};

const blankForm = () => ({
  switchOffWeekObserved: "",
  septemberConsumption: "",
  consumptionUnit: "units",
  fourBinInstalled: "",
  bins: BIN_SLOTS.map((b) => ({ colour: b.colour, category: b.category })),
  notificationDisplayed: "",
  postersDisplayed: "",
  activities: [],
  activitiesOther: "",
  studentsParticipated: "",
  teachersParticipated: "",
});

/* ── small building blocks ──────────────────────────────────────────────── */

const YesNo = ({ name, value, onChange, disabled }) => (
  <div className="bwr-yesno" role="radiogroup" aria-label={name}>
    {["yes", "no"].map((v) => (
      <button
        key={v}
        type="button"
        role="radio"
        aria-checked={value === v}
        disabled={disabled}
        className={`bwr-yesno__btn${value === v ? " is-on" : ""} bwr-yesno__btn--${v}`}
        onClick={() => onChange(v)}
      >
        {v === "yes" ? "Yes" : "No"}
      </button>
    ))}
  </div>
);

// Every one of the seven is mandatory — the two that are answered from the
// school's own records are simply answered already.
const Question = ({ n, title, note, hint, children, error, locked = false }) => (
  <section className={`bwr-q${error ? " has-error" : ""}`} id={`bwr-q${n}`}>
    <div className="bwr-q__head">
      <span className="bwr-q__n">{n}</span>
      <div className="bwr-q__titles">
        <h4 className="bwr-q__title">
          {title}
          {!locked && <span className="bwr-req" aria-hidden="true"> *</span>}
        </h4>
        {hint && <p className="bwr-q__hint">{hint}</p>}
      </div>
    </div>
    <div className="bwr-q__body">{children}</div>
    {note && (
      <p className="bwr-note">
        <strong>Remark:</strong> {note}
      </p>
    )}
    {error && (
      <p className="bwr-err">
        <FaExclamationTriangle /> {error}
      </p>
    )}
  </section>
);

const FetchedChip = ({ text }) => (
  <span className="bwr-fetched">
    <FaLock /> {text}
  </span>
);

/* ── the form ───────────────────────────────────────────────────────────── */

export default function BricsWeekReportForm({
  report,
  prefill,
  onSubmitted,
  canSubmit = true,
  closedMessage = "",
  // Without a login the UDISE has to travel in the body, and the server allows
  // one submission from there — a correction is a school-login action.
  isPublic = false,
  udiseId = "",
  onRequireLogin = null,
}) {
  const [form, setForm] = useState(blankForm);
  const [photos, setPhotos] = useState([]);
  const [notifFiles, setNotifFiles] = useState([]);
  const [busyPhotos, setBusyPhotos] = useState(0);
  const [busyNotif, setBusyNotif] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [done, setDone] = useState(false);
  const photoInput = useRef(null);
  const notifInput = useRef(null);

  const [submitWeekReport, { isLoading: savingAuthed }] = useSubmitBricsWeekReportMutation();
  const [submitPublicWeekReport, { isLoading: savingPublic }] = useSubmitPublicBricsWeekReportMutation();
  const isLoading = savingAuthed || savingPublic;

  const fourBin = prefill?.fourBin || {};
  const notification = prefill?.notification || {};
  const binLocked = Boolean(fourBin.locked);
  const notifLocked = Boolean(notification.locked);

  // What the school already sent, so reopening the form shows it rather than a
  // blank page. The locked questions always come from the prefill, never the
  // saved copy — the source of truth may have changed since.
  useEffect(() => {
    const w = report?.weekReport;
    setForm((prev) => ({
      ...prev,
      switchOffWeekObserved: w?.switchOffWeekObserved || "",
      septemberConsumption:
        w?.septemberConsumption === null || w?.septemberConsumption === undefined
          ? ""
          : String(w.septemberConsumption),
      consumptionUnit: w?.consumptionUnit || "units",
      fourBinInstalled: binLocked ? fourBin.answer : w?.fourBinInstalled || "",
      bins:
        Array.isArray(w?.bins) && w.bins.length
          ? BIN_SLOTS.map((b, i) => ({
              colour: w.bins[i]?.colour ?? b.colour,
              category: w.bins[i]?.category ?? b.category,
            }))
          : prev.bins,
      notificationDisplayed: notifLocked ? "yes" : w?.notificationDisplayed || "",
      postersDisplayed: w?.postersDisplayed || "",
      activities: Array.isArray(w?.activities) ? w.activities : prev.activities,
      activitiesOther: w?.activitiesOther || "",
      studentsParticipated: w?.studentsParticipated ? String(w.studentsParticipated) : "",
      teachersParticipated: w?.teachersParticipated ? String(w.teachersParticipated) : "",
    }));
    if (Array.isArray(w?.photos) && w.photos.length) {
      setPhotos(w.photos.map((p) => ({ url: p.url, lat: p.lat, lng: p.lng, preview: p.url })));
    }
    if (!notifLocked && w?.notificationFile) setNotifFiles([w.notificationFile]);
  }, [report, binLocked, notifLocked, fourBin.answer]);

  const alreadySubmitted = report?.weekReport?.status === "submitted";
  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: "" } : e));
    setBanner("");
  };
  const toggleActivity = (key) =>
    setForm((p) => {
      const on = p.activities.includes(key);
      const activities = on ? p.activities.filter((k) => k !== key) : [...p.activities, key];
      setErrors((e) => (e.activities ? { ...e, activities: "" } : e));
      setBanner("");
      // dropping "Others" drops what was typed under it
      return { ...p, activities, activitiesOther: activities.includes("other") ? p.activitiesOther : "" };
    });

  const setBin = (i, field, v) =>
    setForm((p) => ({ ...p, bins: p.bins.map((b, j) => (j === i ? { ...b, [field]: v } : b)) }));

  const geoMissing = useMemo(() => photos.some((p) => p.lat == null || p.lng == null), [photos]);

  /* ── Q8 photos: the photo's own GPS, else the device's ─────────────────── */
  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || busyPhotos) return;
    const room = PHOTOS_MAX - photos.length;
    if (room <= 0) {
      setErrors((e) => ({ ...e, photos: `You can upload at most ${PHOTOS_MAX} photographs.` }));
      return;
    }
    const picked = files.slice(0, room);
    setBusyPhotos(picked.length);
    setErrors((e) => ({ ...e, photos: "" }));
    try {
      let coords;
      const prepared = [];
      for (const file of picked) {
        if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
          throw new Error(`"${file.name}" is not a JPG or PNG photograph.`);
        }
        let lat = NaN;
        let lng = NaN;
        try {
          const tags = await ExifReader.load(await file.arrayBuffer());
          lat = extractGPS(tags?.GPSLatitude, tags?.GPSLatitudeRef);
          lng = extractGPS(tags?.GPSLongitude, tags?.GPSLongitudeRef);
        } catch (e) {
          /* no EXIF block — the device location below covers it */
        }
        let out = file;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          if (coords === undefined) {
            try {
              coords = await gps.getCurrentCoords();
            } catch (e) {
              coords = null;
            }
          }
          if (coords && Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude)) {
            lat = coords.latitude;
            lng = coords.longitude;
            if (typeof gps.embedGpsExif === "function") {
              try {
                out = await gps.embedGpsExif(file, lat, lng);
              } catch (e) {
                /* keep the original bytes */
              }
            }
          }
        }
        if (out.size > PHOTO_MB * 1024 * 1024) {
          const blob = await imageCompression(out, {
            maxSizeMB: PHOTO_MB - 1,
            maxWidthOrHeight: 2560,
            useWebWorker: true,
            preserveExif: true,
          });
          out = new File([blob], out.name, { type: out.type || file.type });
        }
        prepared.push({
          file: new File([out], safeName(out, "photo"), { type: out.type || file.type }),
          lat: Number.isFinite(lat) ? lat : null,
          lng: Number.isFinite(lng) ? lng : null,
          preview: URL.createObjectURL(out),
        });
      }
      const added = [];
      for (let i = 0; i < prepared.length; i += 3) {
        const chunk = prepared.slice(i, i + 3);
        const fd = new FormData();
        chunk.forEach((p) => fd.append("files", p.file));
        const urls = await uploadFiles(fd);
        urls.forEach((url, j) => chunk[j] && added.push({ url, lat: chunk[j].lat, lng: chunk[j].lng, preview: chunk[j].preview }));
      }
      setPhotos((prev) => [...prev, ...added].slice(0, PHOTOS_MAX));
    } catch (err) {
      setErrors((e) => ({ ...e, photos: err?.response?.data?.message || err?.message || "Upload failed. Please try again." }));
    } finally {
      setBusyPhotos(0);
      if (photoInput.current) photoInput.current.value = "";
    }
  };

  /* ── Q4: upload the notification here when the school has none ─────────── */
  const addNotification = async (file) => {
    if (!file || busyNotif) return;
    setBusyNotif(true);
    setErrors((e) => ({ ...e, notificationDisplayed: "" }));
    try {
      if (file.type !== "application/pdf") throw new Error("Upload the notification as a PDF file.");
      if (file.size > PHOTO_MB * 1024 * 1024) throw new Error(`The file must be smaller than ${PHOTO_MB} MB.`);
      const fd = new FormData();
      fd.append("files", file);
      const urls = await uploadFiles(fd, "upload-notification-pdf");
      setNotifFiles(urls.slice(0, 1));
      set("notificationDisplayed", "yes");
    } catch (err) {
      setErrors((e) => ({
        ...e,
        notificationDisplayed: err?.response?.data?.message || err?.message || "Could not upload the notification.",
      }));
    } finally {
      setBusyNotif(false);
      if (notifInput.current) notifInput.current.value = "";
    }
  };

  /* ── validation: the same rules the server enforces ────────────────────── */
  const validate = () => {
    const e = {};
    if (!form.switchOffWeekObserved) e.switchOffWeekObserved = "Tell us whether the one-hour switch-off was held daily for a week.";

    const units = form.septemberConsumption;
    if (units === "") e.septemberConsumption = "Enter the electricity consumption for September.";
    else if (!/^\d+(\.\d+)?$/.test(units)) e.septemberConsumption = "Enter the figure in numbers only.";
    else if (Number(units) > UNITS_MAX) e.septemberConsumption = `The figure cannot be more than ${UNITS_MAX.toLocaleString("en-IN")}.`;

    if (!binLocked && !form.fourBinInstalled) e.fourBinInstalled = "Tell us whether the four-bin system is installed.";
    if (form.fourBinInstalled === "yes" && form.bins.some((b) => !b.colour.trim() || !b.category.trim())) {
      e.fourBinInstalled = "Give the colour and the waste category for each of the four bins.";
    }

    if (!notifLocked) {
      if (!form.notificationDisplayed) e.notificationDisplayed = "Tell us whether the notification is on the notice board.";
      else if (form.notificationDisplayed === "yes" && !notifFiles.length) {
        e.notificationDisplayed = "Upload the signed Eco Club notification.";
      }
    }

    if (!form.postersDisplayed) e.postersDisplayed = "Tell us whether the posters and theme details are on the notice board.";

    if (!form.activities.length) e.activities = "Select at least one activity conducted on the Mission LiFE themes.";
    else if (form.activities.includes("other") && !form.activitiesOther.trim()) {
      e.activities = "Name the other activity you conducted.";
    }

    for (const [k, label] of [["studentsParticipated", "students"], ["teachersParticipated", "teachers"]]) {
      const v = form[k];
      if (v === "" || !/^\d+$/.test(v)) e[k] = `Enter the number of ${label} who took part (0–${PARTICIPANTS_MAX}).`;
      else if (Number(v) > PARTICIPANTS_MAX) e[k] = `Cannot be more than ${PARTICIPANTS_MAX}.`;
    }

    if (photos.length < PHOTOS_MIN) e.photos = `Upload at least ${PHOTOS_MIN} geotagged photographs.`;
    else if (geoMissing) e.photos = "Every photograph must carry a location. Allow location access and upload again.";

    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    setErrors(e);
    const first = Object.keys(e)[0];
    if (first) {
      setBanner(e[first]);
      document.getElementById(`bwr-f-${first}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      const send = isPublic ? submitPublicWeekReport : submitWeekReport;
      const res = await send({
        ...(isPublic ? { udise_id: udiseId } : {}),
        switchOffWeekObserved: form.switchOffWeekObserved,
        septemberConsumption: Number(form.septemberConsumption),
        consumptionUnit: form.consumptionUnit,
        fourBinInstalled: form.fourBinInstalled,
        bins: form.fourBinInstalled === "yes" ? form.bins : [],
        notificationDisplayed: form.notificationDisplayed,
        notificationFiles: notifFiles,
        postersDisplayed: form.postersDisplayed,
        activities: form.activities,
        activitiesOther: form.activities.includes("other") ? form.activitiesOther.trim() : "",
        studentsParticipated: Number(form.studentsParticipated),
        teachersParticipated: Number(form.teachersParticipated),
        photos: photos.map((p) => ({ url: p.url, lat: p.lat, lng: p.lng })),
      }).unwrap();
      setDone(true);
      setBanner("");
      onSubmitted?.(res);
      document.getElementById("bwr-top")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      const d = err?.data || {};
      if (d.code === "BRICS_LOGIN_TO_EDIT") onRequireLogin?.();
      setBanner(d.errors?.[0] || d.message || "Could not submit your report. Please try again.");
    }
  };

  if (done) {
    return (
      <div className="bwr" id="bwr-top">
        <div className="bwr-done">
          <FaCheckCircle />
          <h3>Report submitted</h3>
          <p>
            Thank you. Your school's BRICS Sustainable Lifestyle Week report has been recorded. To correct anything,
            sign in to the school dashboard and open this form again.
          </p>
          <button type="button" className="bwr-submit bwr-submit--ghost" onClick={() => setDone(false)}>
            View / edit my answers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bwr" id="bwr-top">
      <div className="bwr-head">
        <div>
          <h3 className="bwr-head__title">School Reporting</h3>
          <p className="bwr-head__sub">
            Eight questions on what your school did during BRICS Sustainable Lifestyle Week, 1–7 November 2026.
          </p>
        </div>
        {alreadySubmitted && (
          <span className="bwr-head__badge">
            <FaCheckCircle /> {isPublic ? "Already submitted" : "Submitted — you are editing it"}
          </span>
        )}
      </div>

      {isPublic && alreadySubmitted && (
        <div className="bwr-locked" role="status">
          <FaLock />
          <span>
            This school has already sent its report. To change an answer, sign in to the school dashboard.
          </span>
          {onRequireLogin && (
            <button type="button" className="bwr-btn" onClick={onRequireLogin}>
              Login to update
            </button>
          )}
        </div>
      )}

      {banner && (
        <div className="bwr-banner" role="alert">
          <FaExclamationTriangle /> {banner}
        </div>
      )}

      {/* ── Q1 ───────────────────────────────────────────────────────────── */}
      <Question
        n={1}
        title="Has the school conducted the one-hour switch-off activity (lights, fans, etc.) daily for one week to promote energy conservation?"
        error={errors.switchOffWeekObserved}
      >
        <div id="bwr-f-switchOffWeekObserved">
          <YesNo name="Q1" value={form.switchOffWeekObserved} onChange={(v) => set("switchOffWeekObserved", v)} />
        </div>
      </Question>

      {/* ── Q2 ───────────────────────────────────────────────────────────── */}
      <Question
        n={2}
        title="What was the total electricity consumption for the month of September?"
        note="A comparison for the month of September and November will be conducted."
        error={errors.septemberConsumption}
      >
        <div className="bwr-measure" id="bwr-f-septemberConsumption">
          <input
            type="text"
            inputMode="decimal"
            className="bwr-input bwr-input--num"
            placeholder={`0 – ${UNITS_MAX.toLocaleString("en-IN")}`}
            value={form.septemberConsumption}
            onChange={(ev) => set("septemberConsumption", ev.target.value.replace(/[^\d.]/g, "").slice(0, 8))}
          />
          <select
            className="bwr-input bwr-input--unit"
            value={form.consumptionUnit}
            onChange={(ev) => set("consumptionUnit", ev.target.value)}
            aria-label="Unit of measurement"
          >
            <option value="units">Units</option>
            <option value="kwh">kWh</option>
          </select>
        </div>
        <p className="bwr-q__hint">Maximum {UNITS_MAX.toLocaleString("en-IN")}. Enter the figure from your September electricity bill.</p>
      </Question>

      {/* ── Q3 ───────────────────────────────────────────────────────────── */}
      <Question
        n={3}
        title="Has the school installed and implemented the four-bin waste segregation system as per the Solid Waste Management (SWM) Rules, 2026?"
        hint="Please specify the colours and categories of waste displayed on each bin."
        locked={binLocked}
        error={errors.fourBinInstalled}
      >
        <div className="bwr-row" id="bwr-f-fourBinInstalled">
          <YesNo name="Q3" value={form.fourBinInstalled} onChange={(v) => set("fourBinInstalled", v)} disabled={binLocked} />
          {binLocked && <FetchedChip text={fourBin.note || "Fetched from your Solid Waste Management report."} />}
        </div>

        {form.fourBinInstalled === "yes" && (
          <div className="bwr-bins">
            {BIN_SLOTS.map((slot, i) => (
              <div className="bwr-bin" key={slot.key}>
                <span className="bwr-bin__dot" style={{ background: slot.swatch }} aria-hidden="true" />
                <input
                  className="bwr-input"
                  value={form.bins[i]?.colour ?? ""}
                  onChange={(ev) => setBin(i, "colour", ev.target.value.slice(0, 60))}
                  placeholder="Bin colour"
                  aria-label={`Bin ${i + 1} colour`}
                />
                <input
                  className="bwr-input"
                  value={form.bins[i]?.category ?? ""}
                  onChange={(ev) => setBin(i, "category", ev.target.value.slice(0, 120))}
                  placeholder="Category of waste"
                  aria-label={`Bin ${i + 1} waste category`}
                />
              </div>
            ))}
          </div>
        )}
      </Question>

      {/* ── Q4 ───────────────────────────────────────────────────────────── */}
      <Question
        n={4}
        title="Has the school displayed the notification for establishment of Eco Clubs for Mission LiFE on their notice board?"
        locked={notifLocked}
        error={errors.notificationDisplayed}
      >
        <div className="bwr-row" id="bwr-f-notificationDisplayed">
          <YesNo
            name="Q4"
            value={form.notificationDisplayed}
            onChange={(v) => set("notificationDisplayed", v)}
            disabled={notifLocked}
          />
          {notifLocked && <FetchedChip text={notification.note || "Fetched from the notification you uploaded."} />}
          {!notifLocked && notifFiles.length > 0 && (
            <span className="bwr-link bwr-link--plain">
              <FaFilePdf /> Notification uploaded
            </span>
          )}
        </div>

        {!notifLocked && (
          <div className="bwr-notif">
            <p className="bwr-q__hint">
              Your school has not uploaded the notification yet. Download the format, get it signed, and upload it here —
              it is saved to your school record at the same time.
            </p>
            <div className="bwr-notif__actions">
              <a className="bwr-btn bwr-btn--ghost" href={NOTIFICATION_TEMPLATE} download="Eco_Club_Notification.docx" target="_blank" rel="noopener noreferrer">
                <FaDownload /> Download format
              </a>
              <button type="button" className="bwr-btn" onClick={() => notifInput.current?.click()} disabled={busyNotif}>
                {busyNotif ? <FaSpinner className="bwr-spin" /> : <FaCloudUploadAlt />}
                {notifFiles.length ? "Replace PDF" : "Upload signed PDF"}
              </button>
              <input
                ref={notifInput}
                type="file"
                accept="application/pdf"
                hidden
                onChange={(ev) => addNotification(ev.target.files?.[0])}
              />
            </div>
          </div>
        )}
      </Question>

      {/* ── Q5 ───────────────────────────────────────────────────────────── */}
      <Question
        n={5}
        title="Has the school displayed posters, awareness materials, and details of activities conducted for 7 themes on the school notice board?"
        error={errors.postersDisplayed}
      >
        <div id="bwr-f-postersDisplayed">
          <YesNo name="Q5" value={form.postersDisplayed} onChange={(v) => set("postersDisplayed", v)} />
        </div>
      </Question>

      {/* ── Q6 ───────────────────────────────────────────────────────────── */}
      <Question
        n={6}
        title="Activities conducted on Mission LiFE themes"
        hint="Tick every activity your school held. You can tick more than one."
        error={errors.activities}
      >
        <div className="bwr-checks" id="bwr-f-activities">
          {ACTIVITIES.map((a) => {
            const on = form.activities.includes(a.key);
            return (
              <button
                key={a.key}
                type="button"
                role="checkbox"
                aria-checked={on}
                className={`bwr-check${on ? " is-on" : ""}`}
                onClick={() => toggleActivity(a.key)}
              >
                <span className="bwr-check__box" aria-hidden="true">
                  {on && <FaCheck />}
                </span>
                {a.label}
              </button>
            );
          })}
        </div>
        {form.activities.includes("other") && (
          <input
            type="text"
            className="bwr-input bwr-input--other"
            placeholder="Name the other activity"
            value={form.activitiesOther}
            onChange={(ev) => set("activitiesOther", ev.target.value.slice(0, ACTIVITY_OTHER_MAX))}
            aria-label="Other activity"
          />
        )}
      </Question>

      {/* ── Q7 ───────────────────────────────────────────────────────────── */}
      <Question
        n={7}
        title="Participation details"
        hint={`How many students and teachers took part in the activities? Up to ${PARTICIPANTS_MAX} each.`}
        error={errors.studentsParticipated || errors.teachersParticipated}
      >
        <div className="bwr-counts">
          <label className="bwr-count" id="bwr-f-studentsParticipated">
            <span>Students</span>
            <input
              type="text"
              inputMode="numeric"
              className="bwr-input bwr-input--num"
              placeholder={`0 – ${PARTICIPANTS_MAX}`}
              value={form.studentsParticipated}
              onChange={(ev) => set("studentsParticipated", ev.target.value.replace(/\D/g, "").slice(0, 4))}
            />
          </label>
          <label className="bwr-count" id="bwr-f-teachersParticipated">
            <span>Teachers</span>
            <input
              type="text"
              inputMode="numeric"
              className="bwr-input bwr-input--num"
              placeholder={`0 – ${PARTICIPANTS_MAX}`}
              value={form.teachersParticipated}
              onChange={(ev) => set("teachersParticipated", ev.target.value.replace(/\D/g, "").slice(0, 4))}
            />
          </label>
        </div>
      </Question>

      {/* ── Q8 ───────────────────────────────────────────────────────────── */}
      <Question
        n={8}
        title="Geotagged photographs"
        hint={`Upload ${PHOTOS_MIN} to ${PHOTOS_MAX} photographs. A photo without GPS is stamped with your current location, so allow location access.`}
        error={errors.photos}
      >
        <div className="bwr-photos" id="bwr-f-photos">
          {photos.map((p, i) => (
            <div className="bwr-photo" key={p.url || i}>
              <img src={p.preview || p.url} alt={`Photograph ${i + 1}`} />
              <span className={`bwr-photo__geo${p.lat == null ? " is-missing" : ""}`}>
                <FaMapMarkerAlt />
                {p.lat == null ? "No location" : `${Number(p.lat).toFixed(4)}, ${Number(p.lng).toFixed(4)}`}
              </span>
              <button type="button" className="bwr-photo__x" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))} aria-label="Remove photograph">
                <FaTrash />
              </button>
            </div>
          ))}
          {photos.length < PHOTOS_MAX && (
            <button type="button" className="bwr-photo bwr-photo--add" onClick={() => photoInput.current?.click()} disabled={busyPhotos > 0}>
              {busyPhotos ? <FaSpinner className="bwr-spin" /> : <FaCloudUploadAlt />}
              <span>{busyPhotos ? `Uploading ${busyPhotos}…` : "Add photograph"}</span>
            </button>
          )}
          <input ref={photoInput} type="file" accept="image/jpeg,image/jpg,image/png" multiple hidden onChange={(ev) => addPhotos(ev.target.files)} />
        </div>
        <p className="bwr-q__hint">
          {photos.length} of {PHOTOS_MAX} uploaded{photos.length < PHOTOS_MIN ? ` — ${PHOTOS_MIN - photos.length} more needed` : ""}.
        </p>
      </Question>

      <div className="bwr-foot">
        {!canSubmit && closedMessage && (
          <p className="bwr-foot__closed">
            <FaExclamationTriangle /> {closedMessage}
          </p>
        )}
        <button
          type="button"
          className="bwr-submit"
          onClick={handleSubmit}
          disabled={isLoading || !canSubmit || (isPublic && alreadySubmitted)}
        >
          {isLoading ? <FaSpinner className="bwr-spin" /> : <FaCheckCircle />}
          {isLoading ? "Submitting…" : alreadySubmitted ? "Update my report" : "Submit report"}
        </button>
        <p className="bwr-foot__note">
          {isPublic
            ? "Once sent, changing an answer needs the school login."
            : "You can come back and edit this any time during the week by signing in to the school dashboard."}
        </p>
      </div>
    </div>
  );
}
